import { useEffect, useRef, useState } from 'react';

import { Done, Close, Edit } from '@mui/icons-material';
import cloneDeep from 'lodash/cloneDeep';
import { get, Path } from 'react-hook-form';

import { useFormContextHelper, useFormContextHelperProps } from './useFormContextHelper';

export type GenerateFieldType<T> = (fieldName: Path<T>) => {
  isEdited: any;
  isEditing: boolean;
  value: any;
  actions: {
    disabled: boolean;
    onClick: () => void;
    icon: JSX.Element;
  }[];
};

interface FieldGeneratorReturn<T> {
  editingKey: Path<T>;
  generateField: GenerateFieldType<T>;
}

export function useFieldGenerator<T>(
  params?: Partial<useFormContextHelperProps<T>>,
): FieldGeneratorReturn<T> {
  const { getValues, setValue, formState } = useFormContextHelper<T>(params);
  const { dirtyFields, errors } = formState || {};
  const [editingKey, setEditingKey] = useState(null);
  const oldEditValue = useRef(null);

  useEffect(() => {
    if (editingKey) {
      oldEditValue.current = cloneDeep(getValues(editingKey));
    }
  }, [editingKey, getValues]);
  return {
    editingKey,
    generateField: (fieldName: Path<T>) => {
      const value = getValues(fieldName) as any;
      return {
        isEdited: get(dirtyFields, fieldName as string),
        isEditing: fieldName === editingKey,
        value:
          typeof value === 'string' || typeof value === 'number'
            ? value
            : typeof value === 'boolean'
            ? value.toString()
            : value?.id
            ? `${value?.name ?? value?.officialName ?? ''} - ${value?.id}`
            : value?.name ?? value?.officialName,
        actions: [
          editingKey === fieldName && {
            disabled: !!get(errors, fieldName as string),
            onClick: () => setEditingKey(null),
            icon: <Done />,
          },
          {
            disabled: editingKey !== null && editingKey !== fieldName,
            onClick: () => {
              if (editingKey !== null) {
                if (oldEditValue.current !== null) {
                  setValue(editingKey, oldEditValue.current, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
                setEditingKey(null);
              } else {
                setEditingKey(fieldName);
              }
            },
            icon: editingKey !== null && fieldName === editingKey ? <Close /> : <Edit />,
          },
        ],
      };
    },
  };
}
